/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-danger */
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';
import { getComponentFromName, getImageAtSize } from '../../lib/utils';

import { useLabels } from '../../contexts/LabelsContext';
import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import StyledButton from '../buttons/StyledButton';
import * as IconComponents from '../icons/index';
import PrizeCard from '../partials/PrizeCard';

import styles from '../../../styles/blocks/draw.module.scss';

import points from '../../../img/iga/2024/points.png';

const propTypes = {
    handle: PropTypes.string,
    isContainer: PropTypes.bool,
    containerClassName: PropTypes.string,
    image: AppPropTypes.image,
    titleImage: AppPropTypes.image,
    titleUrl: PropTypes.string,
    titleDescription: PropTypes.string,
    description: PropTypes.string,
    prizes: PropTypes.arrayOf(PropTypes.string),
    badge: PropTypes.string,
    title: PropTypes.string,
    drawingAt: PropTypes.string,
    drawingStartAt: PropTypes.string,
    date: PropTypes.string,
    className: PropTypes.string,
};

const defaultProps = {
    handle: null,
    isContainer: false,
    containerClassName: null,
    image: null,
    titleImage: null,
    titleUrl: null,
    titleDescription: null,
    description: null,
    prizes: null,
    badge: null,
    title: null,
    drawingAt: null,
    drawingStartAt: null,
    date: null,
    className: null,
};

const DrawBlock = ({
    handle,
    isContainer,
    containerClassName,
    image,
    titleImage,
    titleUrl,
    titleDescription,
    description,
    prizes,
    badge,
    title,
    drawingAt,
    drawingStartAt,
    date,
    className,
}) => {
    const { name: theme, isVariant } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const intl = useIntl();
    const { drawBlockLearnMore = null } = useLabels();
    const { locale = 'fr' } = intl || {};

    const { url: imageUrl = null, alt: imageAlt } =
        getImageAtSize(image || null, 'medium', false) || {};
    const { url: titleImageUrl = null, alt: titleImageAlt } =
        getImageAtSize(titleImage || null, 'medium', false) || {};

    const drawingAtDate = useMemo(
        () => (drawingAt !== null ? parseISO(drawingAt) : null),
        [drawingAt],
    );
    const drawingStartAtDate = useMemo(
        () => (drawingStartAt !== null ? parseISO(drawingStartAt) : null),
        [drawingStartAt],
    );
    const BadgeComponent = badge !== null ? getComponentFromName(IconComponents, badge) : null;

    const invertedDescription = theme === 'iga';
    const withYear = theme === 'iga' || theme === 'voisin' || theme === 'iga_noel';

    let dateElement = date;
    if (drawingAtDate !== null) {
        dateElement = (
            <FormattedMessage
                defaultMessage="Draw on {drawing_at}"
                description="Draw date"
                values={{
                    drawing_at: intl.formatDate(drawingAtDate, {
                        day: 'numeric',
                        month: 'long',
                        ...(withYear ? { year: 'numeric' } : {}),
                    }),
                }}
            />
        );
    } else if (drawingStartAtDate !== null) {
        dateElement = (
            <FormattedMessage
                defaultMessage="Draws starting on {date}"
                description="Draw date"
                values={{
                    date: intl.formatDate(drawingStartAtDate, {
                        day: 'numeric',
                        month: 'long',
                        ...(withYear ? { year: 'numeric' } : {}),
                    }),
                }}
            />
        );
    }

    const header =
        title !== null || dateElement !== null ? (
            <div className={classNames([styles.header, 'block-header'])}>
                {title !== null ? (
                    <h4
                        className={classNames([styles.title, 'block-title'])}
                        dangerouslySetInnerHTML={{ __html: title }}
                    />
                ) : null}
                {titleDescription !== null ? (
                    <div
                        className={classNames([styles.description, 'block-title-description'])}
                        dangerouslySetInnerHTML={{ __html: titleDescription }}
                    />
                ) : null}
                {titleImageUrl !== null ? (
                    <div className={styles.imageContainer}>
                        <div className={styles.frame}>
                            {titleUrl !== null ? (
                                <a href={titleUrl} target="_blank" rel="noopener noreferrer">
                                    <img
                                        className={classNames([
                                            styles.titleImage,
                                            'block-title-image',
                                        ])}
                                        src={titleImageUrl}
                                        alt={titleImageAlt}
                                    />
                                </a>
                            ) : (
                                <img
                                    className={classNames([styles.titleImage, 'block-title-image'])}
                                    src={titleImageUrl}
                                    alt={titleImageAlt}
                                />
                            )}
                        </div>
                    </div>
                ) : null}
                {titleUrl !== null ? (
                    <StyledButton className={styles.button} href={titleUrl} external>
                        {drawBlockLearnMore || (
                            <FormattedMessage
                                defaultMessage="Learn more"
                                description="Button label"
                            />
                        )}
                    </StyledButton>
                ) : null}
                {dateElement !== null ? (
                    <div className={classNames([styles.date, 'block-date'])}>{dateElement}</div>
                ) : null}
            </div>
        ) : null;

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                className,
                { [styles[handle]]: handle !== null },
            ])}
        >
            <div
                className={classNames([
                    styles.inner,
                    {
                        [styles.isContainer]: isContainer,
                        [containerClassName]: isContainer && containerClassName !== null,
                    },
                ])}
            >
                <div className={styles.box}>
                    {theme !== 'coorslive' ? header : null}
                    {description !== null && invertedDescription ? (
                        <>
                            <div
                                className={classNames([styles.description, ['block-description']])}
                                dangerouslySetInnerHTML={{ __html: description }}
                            />
                            {theme === 'iga' && isVariant('habs') && handle === 'grandprize' ? (
                                <img className={styles.points} src={points} alt="points" />
                            ) : null}
                        </>
                    ) : null}
                    {imageUrl !== null ? (
                        <div className={styles.imageContainer}>
                            <div className={styles.frame}>
                                <img className={styles.image} src={imageUrl} alt={imageAlt} />
                            </div>
                            {BadgeComponent !== null ? (
                                <BadgeComponent className={styles.badge} />
                            ) : null}
                        </div>
                    ) : null}
                    {theme === 'coorslive' ? header : null}
                    {description !== null && !invertedDescription ? (
                        <>
                            <div
                                className={classNames([styles.description, ['block-description']])}
                                dangerouslySetInnerHTML={{ __html: description }}
                            />
                            {theme === 'iga' && isVariant('habs') && handle === 'grandprize' ? (
                                <img className={styles.points} src={points} alt="points" />
                            ) : null}
                        </>
                    ) : null}
                    {prizes !== null ? (
                        <div className={styles.prizes}>
                            {prizes.map(({ drawingAt: prizeDrawingAt = null, ...prize }, idx) => (
                                <PrizeCard
                                    key={`prize-${prize.id}-${idx + 1}`}
                                    description={
                                        prizeDrawingAt !== null ? (
                                            <FormattedMessage
                                                defaultMessage="Draw on {drawing_at}"
                                                description="Draw date"
                                                values={{
                                                    drawing_at: intl.formatDate(prizeDrawingAt, {
                                                        day: 'numeric',
                                                        month: 'long',
                                                    }),
                                                }}
                                            />
                                        ) : null
                                    }
                                    {...prize}
                                    className={styles.prize}
                                    imageClassName={styles.prizeImage}
                                />
                            ))}
                        </div>
                    ) : null}
                    {theme === 'iga' && isVariant('habs') && handle === 'grandprize' ? (
                        <>
                            <p className={styles.text}>
                                <FormattedMessage
                                    defaultMessage="Not a member?"
                                    description="Button label"
                                />
                            </p>
                            <StyledButton
                                className={styles.button}
                                href={`https://www.iga.net/${locale}/sceneplus`}
                                external
                            >
                                <FormattedMessage
                                    defaultMessage="Create my account"
                                    description="Button label"
                                />
                            </StyledButton>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

DrawBlock.propTypes = propTypes;
DrawBlock.defaultProps = defaultProps;

export default DrawBlock;
